import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DispositionService } from '@nice-devone/agent-sdk';
import { MediaType } from '@nice-devone/common-sdk';
import { Disposition } from '../../types';
import { logger } from '../../utils/logger';

export function useAcwDispositions(skillId?: string, contactId?: string) {
  const { t } = useTranslation();
  const [dispositions, setDispositions] = useState<Disposition[]>([]);
  const [isLoadingDispositions, setIsLoadingDispositions] = useState(true);
  const [dispositionError, setDispositionError] = useState<string | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    async function fetchDispositions() {
      logger.debug('[ACWTimer] fetchDispositions called with:', { skillId, contactId });

      if (!skillId || !contactId) {
        logger.debug('[ACWTimer] Missing skillId or contactId, skipping disposition fetch');
        setIsLoadingDispositions(false);
        return;
      }

      const fetchKey = `${skillId}:${contactId}`;
      if (fetchedKeyRef.current === fetchKey) {
        setIsLoadingDispositions(false);
        return;
      }

      fetchedKeyRef.current = fetchKey;
      setIsLoadingDispositions(true);
      setDispositionError(null);

      try {
        const dispositionService = new DispositionService();
        logger.debug('[ACWTimer] Calling getDispositions API...');
        const dispositionList = await dispositionService.getDispositions(
          skillId,
          MediaType.VOICE,
          contactId
        );
        logger.debug('[ACWTimer] Raw API response:', dispositionList);
        const mappedDispositions: Disposition[] = dispositionList.map((d: any) => ({
          dispositionId: String(d.dispositionId || d.id),
          dispositionName: d.dispositionName || d.name || '',
          isRequired: d.isRequired || false,
          requiresNotes: d.requiresNotes || d.requireNotes || false,
        }));
        setDispositions(mappedDispositions);
        logger.debug('[ACWTimer] Mapped dispositions:', mappedDispositions);
      } catch (err) {
        fetchedKeyRef.current = null;
        logger.error('[ACWTimer] Failed to fetch dispositions:', err);
        setDispositionError(t('acw:disposition.failedToLoad'));
      } finally {
        setIsLoadingDispositions(false);
      }
    }

    void fetchDispositions();
  }, [skillId, contactId, t]);

  return { dispositions, isLoadingDispositions, dispositionError };
}
